import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import api from '../../utils/api';
import StaffHeader from './StaffHeader';
import { fmtTime12, fmtRange12, isoDate } from '../../utils/time';
import { ErrorState, TableSkeleton, StatusPill, fmtDate, fmtTimeRange, humanise } from '../../components/ui';
import { useStaffRecord } from './hrKit';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startDow = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = startDow - 1; i >= 0; i--) cells.push({ date: prevDays - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: d, current: true, full: new Date(year, month, d) });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) cells.push({ date: d, current: false });
  return cells;
}

// Shift chips keep their per-type colours (this is the scheduling view of the worker's month).
const PALETTE = [
  'bg-green-100 text-green-800 border-l-2 border-green-400',
  'bg-blue-100 text-blue-800 border-l-2 border-blue-400',
  'bg-orange-100 text-orange-800 border-l-2 border-orange-400',
  'bg-purple-100 text-purple-800 border-l-2 border-purple-400',
  'bg-cyan-100 text-cyan-800 border-l-2 border-cyan-400',
  'bg-yellow-100 text-yellow-800 border-l-2 border-yellow-400',
  'bg-red-100 text-red-800 border-l-2 border-red-400',
  'bg-emerald-100 text-emerald-800 border-l-2 border-emerald-400',
];
function shiftColor(shiftType) {
  if (!shiftType) return 'bg-slate-100 text-slate-700 border-l-2 border-slate-400';
  let h = 0;
  for (let i = 0; i < shiftType.length; i++) h = ((h << 5) - h + shiftType.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

export default function StaffCalendar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [now, setNow] = useState(new Date());

  const year = now.getFullYear();
  const month = now.getMonth();
  const cells = getMonthGrid(year, month);

  const load = useCallback(() => {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;
    setLoading(true); setLoadError(null);
    api.get(`/shifts?staff_id=${id}&start_date=${startDate}&end_date=${endDate}`)
      // Cancelled shifts aren't happening — don't clutter the staff member's calendar.
      .then((r) => setShifts((Array.isArray(r.data) ? r.data : (r.data?.shifts || [])).filter((s) => s.status !== 'cancelled')))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load this month's shifts. Try again."))
      .finally(() => setLoading(false));
  }, [id, year, month]);
  useEffect(() => { load(); }, [load]);

  const shiftsForDay = (d) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return shifts.filter((s) => (s.shift_date || '').slice(0, 10) === key);
  };
  const todayStr = isoDate(new Date());
  const openShift = (s) => navigate(`/scheduler?shiftId=${s.id}`);

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setNow((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))} aria-label="Previous month" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"><ChevronLeft size={16} aria-hidden="true" /></button>
          <h2 className="min-w-[10rem] text-center text-[15px] font-semibold text-slate-900" aria-live="polite">{MONTHS[month]} {year}</h2>
          <button type="button" onClick={() => setNow((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))} aria-label="Next month" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"><ChevronRight size={16} aria-hidden="true" /></button>
          <button type="button" onClick={() => setNow(new Date())} className="btn-secondary btn-sm">Today</button>
          <button type="button" onClick={() => navigate(`/scheduler?staffId=${id}`)} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Schedule a shift</button>
        </div>

        {loading ? <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={6} cols={7} /></div>
          : loadError ? <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={load} /></div>
            : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <div className="min-w-[700px]">
                  <div className="grid grid-cols-7 border-b border-slate-100">
                    {DAYS.map((d) => <div key={d} className="py-2.5 text-center text-[12px] font-semibold text-slate-500">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7" style={{ gridAutoRows: '120px' }}>
                    {cells.map((cell, idx) => {
                      const dayShifts = cell.current ? shiftsForDay(cell.date) : [];
                      const dateStr = cell.current ? `${year}-${String(month + 1).padStart(2, '0')}-${String(cell.date).padStart(2, '0')}` : null;
                      const isToday = dateStr === todayStr;
                      return (
                        <div key={idx} className={`overflow-hidden border-b border-r border-slate-100 p-1.5 ${!cell.current ? 'bg-slate-50/50' : ''}`}>
                          <p className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? 'bg-primary-600 text-white' : cell.current ? 'text-slate-700' : 'text-slate-400'}`}>
                            {cell.date}
                          </p>
                          <div className="space-y-0.5 overflow-hidden">
                            {dayShifts.slice(0, 3).map((s) => (
                              <button key={s.id} type="button" onClick={() => openShift(s)}
                                className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[10.5px] font-medium ${shiftColor(s.shift_type)}`}
                                title={`${fmtRange12(s.start_time, s.end_time)} · ${s.client_names || ''}`}>
                                {fmtTime12(s.start_time)} {s.shift_type ? humanise(s.shift_type) : 'Shift'}
                              </button>
                            ))}
                            {dayShifts.length > 3 && <p className="pl-1 text-[10.5px] text-slate-500">+{dayShifts.length - 3} more</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

        {!loading && !loadError && shifts.length > 0 && (
          <section aria-labelledby="month-shifts-h" className="rounded-xl border border-slate-200 bg-white">
            <h3 id="month-shifts-h" className="border-b border-slate-100 px-4 py-3 text-[15px] font-semibold text-slate-900">All shifts this month</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[12px] text-slate-500">
                    <th className="px-4 py-2 font-medium">Date</th><th className="px-4 py-2 font-medium">Time</th>
                    <th className="px-4 py-2 font-medium">Clients</th><th className="px-4 py-2 font-medium">Type</th><th className="px-4 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...shifts].sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || '') || String(a.start_time || '').localeCompare(String(b.start_time || ''))).map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="whitespace-nowrap px-4 py-2.5"><Link to={`/scheduler?shiftId=${s.id}`} className="font-medium text-slate-800 hover:text-primary-700 hover:underline">{fmtDate((s.shift_date || '').slice(0, 10))}</Link></td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{fmtTimeRange(s.start_time, s.end_time)}</td>
                      <td className="px-4 py-2.5 text-slate-700">{s.client_names || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{s.shift_type ? humanise(s.shift_type) : '—'}</td>
                      <td className="px-4 py-2.5"><StatusPill status={s.status || 'pending'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
